import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import { getCampaignScopeToXLXSMap } from '@principle-theorem/principle-core';
import { IMarketingCampaign } from '@principle-theorem/principle-core/interfaces';
import { isArray, IXSLXImport, WithRef } from '@principle-theorem/shared';

export interface ICampaignRunUploadHelpDialogRequest {
  campaign: WithRef<IMarketingCampaign>;
}

interface IRequiredColumn {
  header: string;
  key: string;
  description: string;
  example: string;
}

const COLUMN_DESCRIPTION_MAP: Record<
  string,
  Pick<IRequiredColumn, 'description' | 'example'>
> = {
  patientId: {
    description:
      'The unique identifier for the patient in Principle. You can find this by running a Custom Report for the "Patient ID" field.',
    example: 'rWf8yGOh8VHyEi1DaZ8f',
  },
  patientName: {
    description:
      'The full name of the patient, including first and last names.',
    example: 'John Doe',
  },
  patientEmail: {
    description:
      'The email address of the patient. This is used for communication and marketing purposes.',
    example: 'johndoe@gmail.com',
  },
  patientMobile: {
    description:
      'The mobile phone number of the patient. This is used for communication and marketing purposes.',
    example: '+61412345678',
  },
  appointmentId: {
    description:
      'The unique identifier for the appointment in Principle. You can find this by running a Custom Report for the "Appointment ID" field.',
    example: 'WgbSuUcjB3qE090bCqj7',
  },
  invoiceId: {
    description:
      'The unique identifier for the invoice in Principle. You can find this by running a Custom Report for the "Invoice ID" field.',
    example: 'oSM0jsPa2cvzrRJyEBlQ',
  },
  invoiceReference: {
    description: 'The reference number for the invoice.',
    example: 'INV58faf9ee-1d0b-4074-8d58-c4e4693246bd',
  },
};

@Component({
  selector: 'pr-campaign-run-upload-help-dialog',
  templateUrl: './campaign-run-upload-help-dialog.component.html',
  styleUrl: './campaign-run-upload-help-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, MatTableModule],
})
export class CampaignRunUploadHelpDialogComponent {
  data = inject<ICampaignRunUploadHelpDialogRequest>(MAT_DIALOG_DATA);
  columns: IRequiredColumn[];
  displayedColumns = ['header', 'description', 'example'];

  constructor() {
    const uploadMap = getCampaignScopeToXLXSMap(this.data.campaign.brandRef);
    const importer = uploadMap[this.data.campaign.scope];
    this.columns = this._getRequiredColumns(importer);
  }

  private _getRequiredColumns(
    importer: IXSLXImport<unknown>
  ): IRequiredColumn[] {
    return importer.headers.map((col) => {
      const helpData = col.key
        ? COLUMN_DESCRIPTION_MAP[col.key] ?? undefined
        : undefined;
      return {
        description: '',
        example: '',
        header: isArray(col.header) ? col.header.join(', ') : col.header ?? '',
        key: col.key ?? '',
        ...helpData,
      };
    });
  }
}
